import { useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
} from "@tanstack/react-table";
import { cn } from "../lib/utils";
import { StatusChip } from "../components/ui/StatusChip";
import { OwnerTag } from "../components/ui/OwnerTag";
import { PriorityDot } from "../components/ui/PriorityDot";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowRight01Icon } from "@hugeicons/core-free-icons";
import { IconMsgs, IconSitemap, IconSignal } from "nucleo-glass";
import { NucleoIcon } from "../components/ui/NucleoIcon";
import { RedditChannelPage } from "./channels/RedditChannelPage";
import { DirectoriesChannelPage } from "./channels/DirectoriesChannelPage";
import type { ReactNode } from "react";
import type { Card } from "../data/types";
import { getTaskMeta } from "../lib/taskMeta";

type ChannelView = "main" | "reddit" | "directories";

interface ChannelsPageProps {
  cards: Card[];
  onSelectCard: (cardId: string) => void;
}

const columnHelper = createColumnHelper<Card>();

export function ChannelsPage({ cards, onSelectCard }: ChannelsPageProps) {
  const [view, setView] = useState<ChannelView>("main");

  if (view === "reddit") {
    return <RedditChannelPage cards={cards} onBack={() => setView("main")} onSelectCard={onSelectCard} />;
  }
  if (view === "directories") {
    return <DirectoriesChannelPage onBack={() => setView("main")} />;
  }

  return <ChannelsMainView cards={cards} onSelectCard={onSelectCard} onNavigate={setView} />;
}

// ── Main View ───────────────────────────────────────────────────

interface ChannelsMainViewProps {
  cards: Card[];
  onSelectCard: (cardId: string) => void;
  onNavigate: (view: ChannelView) => void;
}

type ChannelTab = "setup" | "daily";

function ChannelsMainView({ cards, onSelectCard, onNavigate }: ChannelsMainViewProps) {
  const channelCards = useMemo(
    () => cards.filter((c) => c.laneId === "channels"),
    [cards],
  );

  const setupChannelCards = useMemo(
    () => channelCards.filter((c) => { const t = getTaskMeta(c).taskType; return t === "one_off" || t === "unknown"; }),
    [channelCards],
  );

  const dailyChannelCards = useMemo(
    () => channelCards.filter((c) => getTaskMeta(c).taskType === "recurring_daily"),
    [channelCards],
  );

  const [activeTab, setActiveTab] = useState<ChannelTab>("setup");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState("");

  const columns = useMemo(
    () => [
      columnHelper.accessor("title", {
        header: "Channel",
        cell: (info) => (
          <span className="text-[var(--color-text-primary)] font-medium">{info.getValue()}</span>
        ),
      }),
      columnHelper.accessor("channelTier", {
        header: "Tier",
        cell: (info) => {
          const tier = info.getValue();
          return tier ? (
            <span className={cn(
              "inline-flex items-center rounded-[var(--radius-sm)] px-[6px] py-[1px] text-[14px] font-medium ring-1 ring-inset",
              tier === "A" ? "bg-[var(--color-accent-subtle-bg)] text-[var(--color-accent-300)] ring-[var(--color-accent-800)]" : "bg-[var(--color-bg-hover)] text-[var(--color-text-muted)] ring-[var(--color-border-stroke)]",
            )}>
              {tier}
            </span>
          ) : (
            <span className="text-[var(--color-text-muted)]">—</span>
          );
        },
      }),
      columnHelper.accessor("channelType", {
        header: "Type",
        cell: (info) => (
          <span className="text-[var(--color-text-secondary)] capitalize">{info.getValue() ?? "—"}</span>
        ),
      }),
      columnHelper.accessor("owner", {
        header: "Owner",
        cell: (info) => <OwnerTag owner={info.getValue()} />,
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => <StatusChip status={info.getValue()} />,
      }),
      columnHelper.accessor("targetDate", {
        header: "Date",
        cell: (info) => {
          const d = info.getValue();
          return d ? (
            <span className="tabular-nums text-[var(--color-text-muted)]">
              {new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
            </span>
          ) : (
            <span className="text-[var(--color-text-muted)]">—</span>
          );
        },
      }),
    ],
    [],
  );

  const tableData = activeTab === "setup" ? setupChannelCards : channelCards;

  const table = useReactTable({
    data: tableData,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const tierCounts = useMemo(() => {
    const counts = { A: 0, B: 0, C: 0, none: 0 };
    for (const c of channelCards) {
      if (c.channelTier === "A") counts.A++;
      else if (c.channelTier === "B") counts.B++;
      else if (c.channelTier === "C") counts.C++;
      else counts.none++;
    }
    return counts;
  }, [channelCards]);

  const redditExecutionCount = useMemo(() => {
    return cards.filter((card) => {
      if (card.laneId !== "channels") return false;
      const channel = (card.channel ?? "").toLowerCase();
      const title = card.title.toLowerCase();
      const meta = getTaskMeta(card);
      return (channel.includes("reddit") || title.includes("reddit")) && meta.taskType !== "unknown";
    }).length;
  }, [cards]);

  return (
    <div className="h-full flex flex-col">
      {/* Search */}
      <div className="shrink-0 px-[var(--spacing-4)] py-[var(--spacing-3)] border-b border-[var(--color-border-stroke)]">
        <input
          type="text"
          placeholder="Search channels…"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className={cn(
            "h-[32px] w-[280px] rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
            "bg-[var(--color-bg-canvas)] px-[var(--spacing-2)]",
            "text-[14px] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)]",
            "focus-visible:outline-none focus-visible:border-[var(--color-accent-focus)]",
            "hover:border-[var(--color-bg-hover)] transition-colors",
          )}
        />
        <div className="flex items-center gap-[var(--spacing-3)] mt-[var(--spacing-2)] text-[14px] text-[var(--color-text-muted)] tabular-nums">
          <NucleoIcon icon={<IconSignal size={14} />} className="text-[var(--color-text-muted)] shrink-0" />
          <span>Tier A: {tierCounts.A}</span>
          <span>·</span>
          <span>Tier B: {tierCounts.B}</span>
          <span>·</span>
          <span>Tier C: {tierCounts.C}</span>
          <span>·</span>
          <span>No Tier: {tierCounts.none}</span>
          <span>·</span>
          <span>{channelCards.length} total</span>
        </div>
      </div>

      {/* Channel hub cards */}
      <div className="shrink-0 px-[var(--spacing-4)] py-[var(--spacing-3)] border-b border-[var(--color-border-stroke)]">
        <div className="flex gap-[var(--spacing-3)]">
          <ChannelHubCard
            title="Reddit"
            subtitle={`${redditExecutionCount} execution slots · Strategy guides`}
            accent="var(--color-state-error)"
            index={0}
            onClick={() => onNavigate("reddit")}
            icon={<NucleoIcon icon={<IconMsgs size={20} />} />}
          />
          <ChannelHubCard
            title="Launch Directories"
            subtitle="112 directories · Prioritized"
            accent="var(--color-accent)"
            index={1}
            onClick={() => onNavigate("directories")}
            icon={<NucleoIcon icon={<IconSitemap size={20} />} />}
          />
        </div>
      </div>

      {/* Tabs: Setup / Daily Ops */}
      <div className="shrink-0 px-[var(--spacing-4)] py-[var(--spacing-2)] border-b border-[var(--color-border-stroke)] flex items-center gap-[var(--spacing-1)]">
        {([
          { key: "setup" as const, label: "Setup Channels", count: setupChannelCards.length },
          { key: "daily" as const, label: "Daily Ops", count: dailyChannelCards.length },
        ]).map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={cn(
              "px-[var(--spacing-3)] py-[var(--spacing-1_5)] rounded-[var(--radius-md)] text-[14px] cursor-pointer",
              "transition-colors duration-[var(--duration-fast)]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
              activeTab === tab.key
                ? "bg-[var(--color-bg-hover)] text-[var(--color-text-primary)] font-medium"
                : "text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]",
            )}
          >
            {tab.label} <span className="tabular-nums ml-[var(--spacing-1)]">{tab.count}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === "daily" ? (
        <div className="flex-1 overflow-y-auto">
          <div className="divide-y divide-[var(--color-border-stroke)]">
            {dailyChannelCards.length === 0 ? (
              <div className="px-[var(--spacing-4)] py-[var(--spacing-6)] text-center text-[14px] text-[var(--color-text-muted)]">
                No daily execution tasks in channels.
              </div>
            ) : (
              dailyChannelCards.map((card) => {
                const meta = getTaskMeta(card);
                return (
                  <button
                    key={card.id}
                    type="button"
                    onClick={() => onSelectCard(card.id)}
                    className={cn(
                      "w-full flex items-center gap-[var(--spacing-3)] px-[var(--spacing-4)] py-[var(--spacing-2_5)]",
                      "text-left hover:bg-[var(--color-bg-hover)] cursor-pointer transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent-focus)]",
                      card.status === "done" && "opacity-50",
                    )}
                  >
                    <PriorityDot priority={card.priority} />
                    <span className={cn("flex-1 text-[14px] truncate", card.status === "done" ? "text-[var(--color-text-muted)] line-through" : "text-[var(--color-text-primary)]")}>
                      {card.title}
                    </span>
                    {meta.executionWindow && (
                      <span className="text-[11px] text-[var(--color-text-muted)] border border-[var(--color-border-stroke)] rounded-[var(--radius-sm)] px-[6px] py-[2px] shrink-0">
                        {meta.executionWindow.replace("_CET", " CET")}
                      </span>
                    )}
                    <OwnerTag owner={card.owner} />
                    <StatusChip status={card.status} />
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : (
      <div className="flex-1 overflow-y-auto">
        <table className="w-full text-[14px]">
          <thead className="sticky top-0 bg-[var(--color-bg-canvas)] z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b border-[var(--color-border-stroke)]">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    className={cn(
                      "text-left font-semibold text-[var(--color-text-muted)] px-[var(--spacing-4)] py-[var(--spacing-2)]",
                      header.column.getCanSort() && "cursor-pointer select-none hover:text-[var(--color-text-primary)]",
                    )}
                  >
                    <div className="flex items-center gap-[var(--spacing-1)]">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {{ asc: " ↑", desc: " ↓" }[header.column.getIsSorted() as string] ?? ""}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, rowIdx) => (
              <tr
                key={row.id}
                onClick={() => onSelectCard(row.original.id)}
                className={cn(
                  "border-b border-[var(--color-border-stroke)] last:border-b-0 hover:bg-[var(--color-bg-hover)] cursor-pointer transition-colors",
                  rowIdx < 10 && "animate-row-in",
                )}
                style={rowIdx < 10 ? { animationDelay: `${rowIdx * 20}ms` } : undefined}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-[var(--spacing-4)] py-[var(--spacing-2)]">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

// ── Hub Card ────────────────────────────────────────────────────

function ChannelHubCard({ title, subtitle, accent, index, onClick, icon }: {
  title: string;
  subtitle: string;
  accent: string;
  index: number;
  onClick: () => void;
  icon?: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex-1 flex items-center gap-[var(--spacing-3)] rounded-[var(--radius-lg)]",
        "border border-[var(--color-border-stroke)] bg-[var(--color-bg-surface)]",
        "px-[var(--spacing-4)] py-[var(--spacing-3)]",
        "hover:bg-[var(--color-bg-hover)] hover:border-[var(--color-bg-hover)]",
        "transition-all duration-[var(--duration-fast)] cursor-pointer text-left",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
        "active:scale-[0.98] transition-transform duration-[var(--duration-fast)]",
        "animate-stagger-in",
      )}
      style={{ animationDelay: `${index * 50}ms` }}
    >
      {icon ? (
        <span className="shrink-0" style={{ color: accent }}>{icon}</span>
      ) : (
        <div className="h-[8px] w-[8px] rounded-full shrink-0" style={{ backgroundColor: accent }} />
      )}
      <div className="flex-1 min-w-0">
        <span className="block text-[14px] font-semibold text-[var(--color-text-primary)]">{title}</span>
        <span className="block text-[14px] text-[var(--color-text-muted)]">{subtitle}</span>
      </div>
      <HugeiconsIcon icon={ArrowRight01Icon} size={16} className="text-[var(--color-text-muted)] shrink-0" />
    </button>
  );
}
